// JoinTrips.js
import React, {useEffect, useState, useRef} from "react";
import ReactDOM from "react-dom";
import TripFilter from "../components/trip-filter-bar";
import TravelPreferencesPage from "../components/TravelPreferencesPage";
import "./JoinTrips.css";
import MultipleJoinTripCard from "../components/MultipleJoinTripCard";
import SingleJoinTripCard from "../components/SingleJoinTripCard";
import {Dialog} from "@mui/material";
import ProfilePage from "../Pages/Profile";
import FilterSidebar from "../components/FilterSidebar";
import dayjs from "dayjs";
import {groupSizeOptions} from "../data/TravelOptions";
import {collection, getDocs, doc, getDoc, deleteDoc} from "firebase/firestore";
import {db} from "../firebase/firebase";
import RegisterPopup from "../components/RegisterPopup";
import {useUser} from "../contexts/UserProvider";
import {fetchSingleUserTrips} from "../firebase/SingleUserTrips";
import TripDetailsModal from "../components/TripCard";
import IndividualTripModal from "../components/IndividualTripModal";
import {getAuth} from "firebase/auth";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useCollection} from "react-firebase-hooks/firestore";
import Tooltip from '@mui/material/Tooltip';

const JoinTripsPage = () => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [isTripPopupOpen, setIsTripPopupOpen] = useState(false);
    const [isTripModalOpen, setIsTripModalOpen] = useState(false);
    const [selectedIndividualTrip, setSelectedIndividualTrip] = useState(null);
    const {profile, loading} = useUser();
    const [expandedSection, setExpandedSection] = useState(null);
    const [events] = useCollection(collection(db, "tripEvents"));
    const [singleTripsSnapshot] = useCollection(collection(db, 'SingleUserTrips'));

    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;

    const [filters, setFilters] = useState({
        style: [],
        region: [],
        dates: [],
    });
    const [categoryFilters, setCategoryFilters] = useState({
        difficulty: [],
        size: [],
        gender: [],
        duration: [],
    });

    const [tripEvents, setTripEvents] = useState([]);
    const [singleUserTrips, setSingleUserTrips] = useState([]);

    const soloScrollRef = useRef(null);
    const groupScrollRef = useRef(null);

    const scrollByAmount = (ref, amount) => {
        if (ref.current) {
            ref.current.scrollBy({left: amount, behavior: "smooth"});
        }
    };

    const getLabels = (input) => {
        if (!input) return [];
        if (!Array.isArray(input)) input = [input];
        return input.map((item) =>
            typeof item === "string" ? item.trim() : item?.label?.trim(),
        );
    };

    const getDates = (input) => {
        if (!Array.isArray(input)) return [];
        return input.map((d) =>
            dayjs(typeof d?.toDate === "function" ? d.toDate() : d),
        );
    };

    const filteredTrips = tripEvents.filter((trip) => {
        const tripStyles = (trip?.style ?? "")
            .toString()
            .split(",")
            .map((s) => s.trim());

        const matchStyle =
            filters.style.length === 0 ||
            filters.style.some((s) => tripStyles.includes(s?.label ?? ""));

        const matchRegion =
            filters.region.length === 0 ||
            filters.region.some((r) => trip.location?.includes(r?.label ?? ""));

        const matchDates =
            filters.dates.length === 0 ||
            filters.dates.some((selectedDate) => {
                const tripDate = trip.timestamp?.toDate?.();
                return tripDate && dayjs(tripDate).isSame(selectedDate, "day");
            });

        const matchDifficulty =
            categoryFilters.difficulty.length === 0 ||
            categoryFilters.difficulty.includes(trip.difficulty);


        const getGroupSizeCategory = (maxParticipants) => {
            if (maxParticipants <= 3) return "pair";
            if (maxParticipants <= 6) return "small";
            return "large";
        };

        const matchSize =
            categoryFilters.size.length === 0 ||
            categoryFilters.size.includes(getGroupSizeCategory(trip.maxParticipants || 0));


        const matchDuration =
            categoryFilters.duration.length === 0 ||
            categoryFilters.duration.includes(trip.duration);

        const matchUsers = !trip.participants.includes(profile.id)

        // Check if trip has capacity for more participants
        const currentParticipants = trip.participants?.length || 0;
        const maxParticipants = trip.maxParticipants || 50; // Default fallback
        const hasCapacity = currentParticipants < maxParticipants;

        return (
            matchStyle &&
            matchRegion &&
            matchDates &&
            matchDifficulty &&
            matchSize &&
            matchDuration &&
            matchUsers &&
            hasCapacity
        );
    });

    const filteredProfiles = singleUserTrips.filter((trip) => {
        const formStyles = getLabels(trip.styles ?? trip.style);
        const formRegions = getLabels(trip.regions ?? trip.region);
        const formDifficulties = getLabels(trip.difficulties ?? trip.difficulty);
        const formDurations = getLabels(trip.duration);
        const formDates = getDates(trip.dates);
        const formSize = getLabels(trip.size ?? trip.groupSize);
        const formGender = trip.gender;
        const formAccessible = trip.accessible === true;

        const selectedStyleLabels = filters.style.map((s) => s?.label?.trim());
        const selectedRegionLabels = filters.region.map((r) => r?.label?.trim());

        const matchStyle =
            selectedStyleLabels.length === 0 ||
            selectedStyleLabels.some((label) => formStyles.includes(label));

        const matchRegion =
            selectedRegionLabels.length === 0 ||
            selectedRegionLabels.some((label) => formRegions.includes(label));

        const matchDates =
            filters.dates.length === 0 ||
            filters.dates.some((d) => formDates.some((fd) => fd.isSame(d, "day")));

        const matchDifficulty =
            categoryFilters.difficulty.length === 0 ||
            categoryFilters.difficulty.some((val) => {
                const selected = (val.label || val || "").toLowerCase().trim();
                return (formDifficulties || []).some((tripVal) => {
                    const value = (tripVal.label || tripVal || "").toLowerCase().trim();
                    return value.includes(selected);
                });
            });

        const matchDuration =
            categoryFilters.duration.length === 0 ||
            categoryFilters.duration.some((d) => formDurations.includes(d?.trim()));

        const matchSize =
            categoryFilters.size.length === 0 ||
            categoryFilters.size.some((s) => formSize.includes(s?.trim?.() ?? s));

        const matchGender =
            categoryFilters.gender.length === 0 ||
            categoryFilters.gender.includes(formGender);

        const matchAccessible = !categoryFilters.accessible || formAccessible;
        const isNotFullSingleTrip = (trip.participants?.length || 0) < 2;

        return (
            matchStyle &&
            matchRegion &&
            matchDates &&
            matchDifficulty &&
            matchDuration &&
            matchSize &&
            matchGender &&
            matchAccessible &&
            isNotFullSingleTrip
        );
    });

    // Arrow state and overflow logic must come after filteredTrips and filteredProfiles
    const [showSoloArrows, setShowSoloArrows] = useState(false);
    const [showGroupArrows, setShowGroupArrows] = useState(false);

    useEffect(() => {
        const checkSoloOverflow = () => {
            const el = soloScrollRef.current;
            if (el) setShowSoloArrows(el.scrollWidth > el.clientWidth);
        };
        checkSoloOverflow();
        window.addEventListener('resize', checkSoloOverflow);
        return () => window.removeEventListener('resize', checkSoloOverflow);
    }, [filteredProfiles]);

    useEffect(() => {
        const checkGroupOverflow = () => {
            const el = groupScrollRef.current;
            if (el) setShowGroupArrows(el.scrollWidth > el.clientWidth);
        };
        checkGroupOverflow();
        window.addEventListener('resize', checkGroupOverflow);
        return () => window.removeEventListener('resize', checkGroupOverflow);
    }, [filteredTrips]);

    useEffect(() => {
        const root = document.getElementById('root');
        if (root) {
            root.scrollTo({top: 0, behavior: "smooth"});
        }
    }, [expandedSection]);

    useEffect(() => {
        const fetchTripsFromFirestore = async () => {
            if (!profile || !events) return;
            try {
                const moddedEvents = [];
                for (const docSnap of events.docs) {
                    const eventData = docSnap.data();
                    const templateSnap = await getDoc(
                        doc(db, "tripTemplates", eventData.templateId),
                    );
                    if (templateSnap.exists()) {
                        moddedEvents.push({
                            id: docSnap.id,
                            ...templateSnap.data(),
                            ...eventData,
                        });
                    }
                }
                setTripEvents(moddedEvents);
            } catch (error) {
                console.error("Error fetching trip events:", error);
            }
        };

        const fetchSoloTrips = async () => {
            if (!singleTripsSnapshot) return;
            try {
                const trips = await fetchSingleUserTrips(singleTripsSnapshot, (trip) => trip.eventOwner != profile.id);
                setSingleUserTrips(trips);
            } catch (e) {
                console.error("Failed to fetch single trips", e);
            }
        };

        fetchTripsFromFirestore();
        fetchSoloTrips();
    }, [profile, events, singleTripsSnapshot]);

    useEffect(() => {
        document.body.style.overflow = isTripPopupOpen ? "hidden" : "";
    }, [isTripPopupOpen]);

    const openProfileDialog = (profile) => {
        setSelectedProfile(profile);
        setIsProfileDialogOpen(true);
    };

    const openIndividualTripModal = (profile) => {
        setSelectedIndividualTrip(profile);
        setIsTripModalOpen(true);
    };

    const closeProfileDialog = () => {
        setSelectedProfile(null);
        setIsProfileDialogOpen(false);
    };

    const openTripPopup = (trip) => {
        setSelectedTrip(trip);
        setIsTripPopupOpen(true);
    };

    const closeTripPopup = () => {
        setSelectedTrip(null);
        setIsTripPopupOpen(false);
    };

    return (
        !loading && (
            <>
                <div className="join-trips-page">
                    <div className="fixed-bar-container">
                        <TripFilter onFilterChange={setFilters}/>
                        <Tooltip
                            title="ספר לנו מה העדפותייך לטיול ועזור לשותף המתאים לך למצוא אותך!"
                            arrow
                            sx={{
                                '& .MuiTooltip-tooltip': {
                                    fontFamily: 'Varela Round, Arial, sans-serif !important',
                                    fontSize: '1rem',
                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                    color: '#222',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                },
                                '& .MuiTooltip-arrow': {
                                    color: 'rgba(255,255,255,0.95)',
                                },
                            }}
                        >
                            <button
                                className="open-popup-button"
                                onClick={() => setIsPopupOpen(true)}
                            >
                                לא מצאת את
                                <br/>
                                מה שחיפשת?
                            </button>
                        </Tooltip>
                    </div>

                    <div className="join-trips-content">
                        <div className="sidebar-container">
                            <FilterSidebar onFilterApply={setCategoryFilters}/>
                        </div>

                        <div className="join-trips-lists-horizontal">
                            {expandedSection !== "group" && expandedSection !== "solo" && (
                                <>
                                    <div className="section-header">
                                        <div className="title-with-subtitle">
                                            <h2 className="section-title">👤 מטיילים שמחפשים שותפים</h2>
                                            <p className="section-subtitle">
                                                מצא שותף לטיול וביחד תוכלו לתכנן את הטיול שמתאים בדיוק לכם.<br/>
                                                לאחר שתבקש להצטרף, ואם תאושר – תתחילו לתכנן יחד את המסלול ולצאת להרפתקה
                                                משותפת!
                                            </p>
                                        </div>
                                        <button
                                            className="show-all-button"
                                            onClick={() => setExpandedSection("solo")}
                                        >
                                            הצג הכל
                                        </button>
                                    </div>
                                    <div className="horizontal-scroll-container">
                                        {showSoloArrows && (
                                            <button
                                                className="scroll-arrow left"
                                                onClick={() => scrollByAmount(soloScrollRef, -300)}
                                                aria-label="Scroll left"
                                            >
                                                <ChevronLeftIcon/>
                                            </button>
                                        )}
                                        <div
                                            className="horizontal-scroll"
                                            ref={soloScrollRef}
                                        >
                                            {filteredProfiles
                                                .filter(profile => {
                                                    const dates = getDates(profile.dates);
                                                    const nd = new Date();
                                                    nd.setHours(0, 0, 0, 0)
                                                    return dates.length && dates.some(date => date >= nd);
                                                })
                                                .map((profile, index) => (
                                                    <SingleJoinTripCard
                                                        key={index}
                                                        profile={profile}
                                                        currentUserId={auth.currentUser?.uid}
                                                        onClick={() => openIndividualTripModal(profile)}
                                                    />
                                                ))}
                                        </div>
                                        {showSoloArrows && (
                                            <button
                                                className="scroll-arrow right"
                                                onClick={() => scrollByAmount(soloScrollRef, 300)}
                                                aria-label="Scroll right"
                                            >
                                                <ChevronRightIcon/>
                                            </button>
                                        )}
                                    </div>

                                    <div className="section-header">
                                        <div className="title-with-subtitle">
                                            <h2 className="section-title">👥 טיולים קבוצתיים להצטרפות</h2>
                                            <p className="section-subtitle">
                                                מצא את הקבוצה שמתאימה בדיוק לך והצטרף לטיול שכבר יוצא לדרך.<br/>
                                                שלח בקשת הצטרפות, ואם תאושר – תצאו לטייל יחד כקבוצה, בלי צורך בתכנון
                                                נוסף!
                                            </p>
                                        </div>
                                        <button
                                            className="show-all-button"
                                            onClick={() => setExpandedSection("group")}
                                        >
                                            הצג הכל
                                        </button>
                                    </div>
                                    <div className="horizontal-scroll-container">
                                        {showGroupArrows && (
                                            <button
                                                className="scroll-arrow left"
                                                onClick={() => scrollByAmount(groupScrollRef, -300)}
                                                aria-label="Scroll left"
                                            >
                                                <ChevronLeftIcon/>
                                            </button>
                                        )}
                                        <div
                                            className="horizontal-scroll"
                                            ref={groupScrollRef}
                                            style={{overflowX: "auto", scrollBehavior: "xsmooth", whiteSpace: "nowrap"}}
                                        >
                                            {filteredTrips
                                                .filter((trip) => trip.timestamp?.toDate() > new Date())
                                                .map((trip, index) => (
                                                    <MultipleJoinTripCard
                                                        key={`trip-${index}`}
                                                        trip={trip}
                                                        onClick={openTripPopup}
                                                    />
                                                ))}
                                        </div>
                                        {showGroupArrows && (
                                            <button
                                                className="scroll-arrow right"
                                                onClick={() => scrollByAmount(groupScrollRef, 300)}
                                                aria-label="Scroll right"
                                            >
                                                <ChevronRightIcon/>
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}

                            {expandedSection === "solo" && (
                                <div id="solo-profiles-full" className="full-list-section">
                                    <div className="section-header">
                                        <h3>👤 כל המטיילים שמחפשים שותפים</h3>
                                        <button
                                            className="close-section-button"
                                            onClick={() => setExpandedSection(null)}
                                        >
                                            ✖
                                        </button>
                                    </div>
                                    <div className="grid-list">
                                        {filteredProfiles
                                            .filter(profile => {
                                                const dates = getDates(profile.dates);
                                                return dates.length && dates.some(date => date > new Date());
                                            })
                                            .map((profile, index) => (
                                                <SingleJoinTripCard
                                                    key={`solo-full-${index}`}
                                                    profile={profile}
                                                    currentUserId={auth.currentUser?.uid}
                                                    onClick={() => openIndividualTripModal(profile)}
                                                />
                                            ))}
                                    </div>
                                </div>
                            )}

                            {expandedSection === "group" && (
                                <div id="group-trips-full" className="full-list-section">
                                    <div className="section-header">
                                        <h3>👥 כל הטיולים הקבוצתיים להצטרפות</h3>
                                        <button
                                            className="close-section-button"
                                            onClick={() => setExpandedSection(null)}
                                        >
                                            ✖
                                        </button>
                                    </div>
                                    <div className="grid-list">
                                        {filteredTrips
                                            .filter((trip) => trip.timestamp?.toDate() > new Date())
                                            .map((trip, i) => (
                                                <MultipleJoinTripCard
                                                    key={`trip-full-${i}`}
                                                    trip={trip}
                                                    onClick={openTripPopup}
                                                />
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="travel-preferences-wrapper">
                        <TravelPreferencesPage
                            isOpen={isPopupOpen}
                            onClose={() => setIsPopupOpen(false)}
                        />
                    </div>

                    <Dialog
                        maxWidth="lg"
                        open={isProfileDialogOpen}
                        onClose={closeProfileDialog}
                    >
                        {selectedProfile && (
                            <ProfilePage
                                profileId={selectedProfile.createdBy}
                                canRequestJoin={true}
                            />
                        )}
                    </Dialog>
                    <IndividualTripModal
                        isOpen={isTripModalOpen}
                        onClose={() => setIsTripModalOpen(false)}
                        profile={selectedIndividualTrip}
                        formData={selectedIndividualTrip?.formData}
                        onOpenProfile={() => openProfileDialog(selectedIndividualTrip)}
                        currentUserId={currentUserId} // Pass current user ID
                    />
                </div>

                {isTripPopupOpen &&
                    selectedTrip &&
                    ReactDOM.createPortal(
                        <TripDetailsModal
                            isOpen={true}
                            onClose={closeTripPopup}
                            trip={selectedTrip}
                            isJoining={true}
                        />,
                        document.body,
                    )}

                <RegisterPopup open={!profile.isRegistered}/>
            </>
        )
    );
};

export default JoinTripsPage;
